import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import moment from 'moment';
import { InvoiceCollectionNotesRow } from './types';

export interface ColumnHeader {
  key: string;
  label: string;
  /** On-screen column width in px (DillColumn width) — used to size XLSX columns proportionally. */
  width?: number;
}

const formatRowForExport = (
  row: InvoiceCollectionNotesRow,
  columns: ColumnHeader[],
  options?: { preserveMultilineText?: boolean }
): Record<string, string | number> => {
  const formatted: Record<string, string | number> = {};
  columns.forEach((header) => {
    const value = row[header.key as keyof InvoiceCollectionNotesRow];
    if (header.key === 'activitySummary' && typeof value === 'string') {
      formatted[header.label] = options?.preserveMultilineText
        ? value
        : value.split('\n').join('; ');
    } else if (typeof value === 'boolean') {
      formatted[header.label] = value ? 'Yes' : 'No';
    } else if (typeof value === 'number' && !isNaN(value)) {
      formatted[header.label] = value;
    } else if (typeof value === 'string') {
      formatted[header.label] = value;
    } else {
      formatted[header.label] = 0;
    }
  });
  return formatted;
};

// MM-DD-YYYY stamp for export file names, e.g. "invoice-collection-notes-report-07-23-2026".
// Dashes instead of slashes since "/" is a path separator in download filenames.
const getExportDateStamp = () => moment().format('MM-DD-YYYY');

export const exportToXlsx = (rows: InvoiceCollectionNotesRow[], columns: ColumnHeader[]) => {
  const formattedRows = rows.map((r) =>
    formatRowForExport(r, columns, { preserveMultilineText: true })
  );
  const worksheet = XLSX.utils.json_to_sheet(formattedRows);

  worksheet['!cols'] = columns.map((column) =>
    column.key === 'activitySummary'
      ? { wch: 70 }
      : { wch: Math.max(12, Math.round((column.width ?? 140) / 7)) }
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Collection Notes');
  XLSX.writeFile(workbook, `invoice-collection-notes-report-${getExportDateStamp()}.xlsx`);
};

export const exportToCsv = (rows: InvoiceCollectionNotesRow[], columns: ColumnHeader[]) => {
  const formattedRows = rows.map((r) => formatRowForExport(r, columns));
  const csv = Papa.unparse(formattedRows, { columns: columns.map((c) => c.label) });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `invoice-collection-notes-report-${getExportDateStamp()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
